import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown, X, ExternalLink } from 'lucide-react';
import type { StockQuote } from '../../types/market';

interface WatchlistTableProps {
  stocks: Array<{ ticker: string; name?: string; quote?: StockQuote }>;
  onRemove: (ticker: string) => void;
  className?: string;
}

/* shared cell style helpers */
const th: React.CSSProperties = {
  padding: '0.625rem 1rem',
  fontSize: '0.68rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.07em',
  color: '#334155',
  background: '#0c111d',
  borderBottom: '1px solid #1a2235',
  whiteSpace: 'nowrap',
};
const thR: React.CSSProperties = { ...th, textAlign: 'right' };

export const WatchlistTable: React.FC<WatchlistTableProps> = ({
  stocks,
  onRemove,
  className = '',
}) => {
  const navigate = useNavigate();

  const fmt$ = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);
  const fmtΔ = (n: number) => `${n >= 0 ? '+' : ''}${fmt$(n)}`;
  const fmtPct = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;

  /* ── Empty state ── */
  if (stocks.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%',
          background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 0.875rem',
        }}>
          <svg width="18" height="18" fill="none" stroke="#3b82f6" strokeWidth="1.75" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </div>
        <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#475569', marginBottom: '0.25rem' }}>
          No stocks yet
        </p>
        <p style={{ fontSize: '0.78rem', color: '#1e3a5f' }}>
          Search for a stock above to add it to this watchlist
        </p>
      </div>
    );
  }

  const Row: React.FC<{ stock: (typeof stocks)[0]; compact?: boolean }> = ({ stock, compact }) => {
    const q = stock.quote;
    const isPos = q ? q.change > 0 : null;
    const isNeg = q ? q.change < 0 : null;
    const chColor  = isPos ? '#10b981' : isNeg ? '#ef4444' : '#94a3b8';
    const chBg     = isPos ? 'rgba(16,185,129,0.1)' : isNeg ? 'rgba(239,68,68,0.1)' : 'rgba(148,163,184,0.08)';
    const chBorder = isPos ? 'rgba(16,185,129,0.25)' : isNeg ? 'rgba(239,68,68,0.25)' : 'rgba(148,163,184,0.15)';
    const Icon = isPos ? TrendingUp : isNeg ? TrendingDown : null;

    const tdBase: React.CSSProperties = {
      padding: compact ? '0.625rem 0.75rem' : '0.75rem 1rem',
      borderBottom: '1px solid #0f1929',
      whiteSpace: 'nowrap',
      verticalAlign: 'middle',
    };

    return (
      <tr
        style={{ cursor: 'pointer', transition: 'background 0.15s' }}
        onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = '#141e30'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
        onClick={() => navigate(`/stock/${stock.ticker}`)}
      >
        {/* Ticker + name */}
        <td style={tdBase}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8, flexShrink: 0,
              background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.625rem', fontWeight: 800, color: '#60a5fa',
              fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-0.01em',
            }}>
              {stock.ticker.slice(0, 3)}
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#f1f5f9', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.02em' }}>
                {stock.ticker}
              </div>
              {!compact && (
                <div style={{ fontSize: '0.72rem', color: '#334155', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {stock.name || '—'}
                </div>
              )}
            </div>
          </div>
        </td>

        {/* Price */}
        <td style={{ ...tdBase, textAlign: 'right' }}>
          {q ? (
            <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#f1f5f9', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-0.02em' }}>
              {fmt$(q.price)}
            </span>
          ) : (
            <div style={{ height: 14, width: 60, borderRadius: 3, marginLeft: 'auto' }} className="skeleton" />
          )}
        </td>

        {/* Change $ */}
        {!compact && (
          <td style={{ ...tdBase, textAlign: 'right' }}>
            {q ? (
              <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: chColor, fontFamily: "'JetBrains Mono', monospace" }}>
                {fmtΔ(q.change)}
              </span>
            ) : (
              <div style={{ height: 12, width: 48, borderRadius: 3, marginLeft: 'auto' }} className="skeleton" />
            )}
          </td>
        )}

        {/* Change % badge */}
        <td style={{ ...tdBase, textAlign: 'right' }}>
          {q ? (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '0.2rem 0.5rem', borderRadius: 99,
              background: chBg, border: `1px solid ${chBorder}`,
              fontSize: '0.75rem', fontWeight: 700, color: chColor,
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              {Icon && <Icon size={11} strokeWidth={2.5} />}
              {fmtPct(q.change_percent)}
            </span>
          ) : (
            <div style={{ height: 22, width: 56, borderRadius: 99, marginLeft: 'auto' }} className="skeleton" />
          )}
        </td>

        {/* Volume — desktop only */}
        {!compact && (
          <td style={{ ...tdBase, textAlign: 'right' }}>
            {q ? (
              <span style={{ fontSize: '0.78rem', color: '#334155', fontFamily: "'JetBrains Mono', monospace" }}>
                {q.volume.toLocaleString()}
              </span>
            ) : (
              <div style={{ height: 12, width: 60, borderRadius: 3, marginLeft: 'auto' }} className="skeleton" />
            )}
          </td>
        )}

        {/* Actions */}
        <td style={{ ...tdBase, textAlign: 'center', width: 48 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
            <button
              onClick={e => { e.stopPropagation(); navigate(`/stock/${stock.ticker}`); }}
              aria-label={`View ${stock.ticker}`}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 7, background: 'transparent', border: 'none', cursor: 'pointer', color: '#334155', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.1)'; e.currentTarget.style.color = '#60a5fa'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#334155'; }}
            >
              <ExternalLink size={13} strokeWidth={2} />
            </button>
            <button
              onClick={e => { e.stopPropagation(); onRemove(stock.ticker); }}
              aria-label={`Remove ${stock.ticker}`}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 7, background: 'transparent', border: 'none', cursor: 'pointer', color: '#334155', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#f87171'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#334155'; }}
            >
              <X size={13} strokeWidth={2.5} />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className={className} style={{ overflowX: 'auto' }}>
      {/* Desktop */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }} className="hidden md:table">
        <thead>
          <tr>
            <th style={th}>Symbol</th>
            <th style={thR}>Price</th>
            <th style={thR}>Change</th>
            <th style={thR}>Change %</th>
            <th style={thR}>Volume</th>
            <th style={{ ...th, width: 72 }} />
          </tr>
        </thead>
        <tbody>
          {stocks.map(s => <Row key={s.ticker} stock={s} />)}
        </tbody>
      </table>

      {/* Mobile */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }} className="md:hidden">
        <thead>
          <tr>
            <th style={th}>Symbol</th>
            <th style={thR}>Price</th>
            <th style={thR}>Change %</th>
            <th style={{ ...th, width: 64 }} />
          </tr>
        </thead>
        <tbody>
          {stocks.map(s => <Row key={s.ticker} stock={s} compact />)}
        </tbody>
      </table>
    </div>
  );
};
